import { CASES } from './constants';
import {
    PAGINATION_STORAGE_KEYS,
    DEFAULT_ITEMS_PER_PAGE,
    DEFAULT_PAGE_SIZE_OPTIONS,
    getStoredItemsPerPage,
    getInitialPaginationRange,
    withPreservedPagination,
    persistItemsPerPage,
} from 'datastage/utils/paginationPreferences.js';

/**
 * Gets the initial user sort from localStorage.
 * Falls back to ['id', 'ASC'] if not found.
 */
const getInitialUserSort = () => {
    const saved = localStorage.getItem('userSort');
    return saved ? JSON.parse(saved) : ['id', 'ASC'];
};

/**
 * Gets the initial items per page from localStorage.
 * Falls back to DEFAULT_ITEMS_PER_PAGE (20) if not found.
 */
const getInitialItemsPerPage = () => {
    return getStoredItemsPerPage(PAGINATION_STORAGE_KEYS.users, DEFAULT_ITEMS_PER_PAGE);
};

// Initialize with stored preference
const initialItemsPerPage = getInitialItemsPerPage();

/**
 * Initial state for the users slice.
 *
 * Key state values:
 * - users: Array of user objects for the grid
 * - filterForm: Search form field values
 * - userForm: Edit/create form field values
 * - drawerMode: 'create' | 'edit' | null - controls which drawer mode is active
 * - refreshUsers: Boolean flag to trigger grid refresh from other components
 * - itemsPerPage: User's persisted page size preference (from localStorage)
 */
const initialState = {
    users: [],
    filterForm: {
        name: '',
        email: '',
        role: '',
    },
    userForm: {
        id: '',
        username: '',
        fullName: '',
        ermUserName: '',
        spaUserRoleIds: '',
        status: '',
        rightslineId: '',
    },
    roleOptions: [],
    statusOptions: [
        { value: 'ACTIVE', label: 'ACTIVE' },
        { value: 'DISABLED', label: 'DISABLED' },
        { value: 'INACTIVE', label: 'INACTIVE' },
    ],
    selectedUser: '',
    totalPages: 0,
    totalItems: 0,
    currentPage: 0,
    filterParam: '',
    userRange: getInitialPaginationRange(initialItemsPerPage),
    userSort: getInitialUserSort(),
    defaultFilterRange: getInitialPaginationRange(initialItemsPerPage),
    itemsPerPage: initialItemsPerPage,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    // Unified drawer mode: 'create' | 'edit' | null
    drawerMode: null,
    // Flag to trigger grid refresh from drawer/other components
    refreshUsers: false,
};

/**
 * Reducer function to manage the users state in the Redux store.
 *
 * It handles actions related to updating users, searching users,
 * managing drawer state, and clearing users.
 *
 * @param {Object} state - The current state of the users.
 * @param {Object} action - The action dispatched to the reducer.
 * @return {Object} The new state of the users after applying the action.
 **/
export function users(state = initialState, action) {
    const { type, payload } = action;
    switch (type) {
        case CASES.UPDATE_USERS: {
            // Persist sort preference to localStorage
            if (payload.userSort) {
                localStorage.setItem('userSort', JSON.stringify(payload.userSort));
            }
            // Persist page size preference to localStorage
            if (payload.itemsPerPage !== undefined) {
                persistItemsPerPage(PAGINATION_STORAGE_KEYS.users, payload.itemsPerPage);
            }
            return { ...state, ...payload };
        }
        case CASES.UPDATE_USERS_SEARCH_FORM: {
            const { data, key } = payload;
            return { ...state, [key]: { ...state[key], ...data } };
        }
        case CASES.UPDATE_USER_FORM: {
            const { data, key } = payload;
            return { ...state, [key]: { ...state[key], ...data } };
        }
        case CASES.CLEAR_USER_FORM: {
            return {
                ...state,
                userForm: { ...initialState.userForm },
                selectedUser: initialState.selectedUser,
            };
        }
        case CASES.CLEAR_USERS: {
            // Preserve pagination preferences when clearing users
            return withPreservedPagination(
                state,
                {
                    ...initialState,
                    roleOptions: state.roleOptions,
                    userSort: state.userSort,
                },
                ['userRange', 'defaultFilterRange']
            );
        }
        default:
            return state;
    }
}
